import { useEffect, useState } from "react";
import { Avatar, Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import AutoAwesomeMosaicOutlinedIcon from "@mui/icons-material/AutoAwesomeMosaicOutlined";
import Header from "../../../../components/header/header";
import ExcelFile from "./excel-file/excel-file";
import "./form-builder.css";
import HtmlForm from "./html-form/html-form";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Preview from "./preview/preview";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { useDispatch, useSelector } from "react-redux";
import { getClientDetailsRequest } from "../../../../redux/clients/slice";
import Loading from "../../../../components/loading/loading";
import { currentUserRequest } from "../../../../redux/users/slice";
import {
  exportClientFormSectionsRequest,
  exportFormTemplateSectionsRequest,
} from "../../../../utils/APICalls";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import ExportTemplateDialog from "./export-dialog";
import ImportTemplateDialog from "./import-dialog";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function TabLabel(data: any) {
  return <>{data?.label}</>;
}

export default function FormBuilder() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [value, setValue] = useState(0);
  const [showLoading, setShowLoading] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { loading, clientDetails } = useSelector((state: any) => state.clients);
  const { currentUser } = useSelector((state: any) => state.user.user);

  useEffect(() => {
    if (!currentUser) {
      dispatch(currentUserRequest());
    }
  }, [currentUser, dispatch]);

  useEffect(() => {
    if (state?.clientId && !clientDetails) {
      dispatch(getClientDetailsRequest({ id: state?.clientId }));
    }
  }, [state, dispatch]); // eslint-disable-line

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const exportFormDownload = async () => {
    try {
      let response;
      setShowLoading(true);
      if (state?.clientId) {
        response = await exportClientFormSectionsRequest(state?.id);
      } else {
        response = await exportFormTemplateSectionsRequest(state?.id);
      }
      setShowLoading(false);
      if (response && response.status === 200) {
        const blob = new Blob([response.data], {
          type: "application/json",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `${state?.displayName}.json`; // file name
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        setShowExportDialog(false);
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      console.error("Download failed", error);
    }
  };

  return (
    <>
      <Header />
      <Box className="container">
        <Box sx={{ mx: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              sx={{ mt: 2 }}
              variant="text"
              startIcon={<ArrowBackOutlinedIcon color="primary" />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            {value === 2 && (
              <Button
                sx={{ mt: 2, fontWeight: "510" }}
                variant="contained"
                startIcon={<DownloadOutlinedIcon />}
                disabled
              >
                Test Excel
              </Button>
            )}
          </Box>
          <Typography className="title">{state?.displayName}</Typography>
          {state?.clientId ? (
            <Box display={"flex"}>
              <Avatar
                sx={{ width: 24, height: 24, mr: 1 }}
                alt={clientDetails?.clientName}
                variant="circular"
                src={clientDetails?.logoUrl}
              />
              <Typography className="text-color">
                {clientDetails?.clientName}
              </Typography>
            </Box>
          ) : (
            <Box display={"flex"}>
              <AutoAwesomeMosaicOutlinedIcon className="con-color" />
              <Typography className="text-color">Template</Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{ m: 2 }}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Box>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="form builder tabs"
            >
              <Tab
                className="tab-label"
                label={<TabLabel label="Excel File" showWarning={false} />}
              />
              <Tab
                className="tab-label"
                label={<TabLabel label="Html Form" showWarning={false} />}
              />
              <Tab
                className="tab-label"
                label={<TabLabel label="Preview" showWarning={false} />}
              />
            </Tabs>
          </Box>
          <Box>
            {value === 1 && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<UploadOutlinedIcon />}
                  onClick={() => setShowExportDialog(true)}
                >
                  <b>Export form Elements</b>
                </Button>
                <Button
                  sx={{ ml: 2 }}
                  variant="outlined"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={() => setShowImportDialog(true)}
                >
                  <b>Import Form Elements</b>
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <CustomTabPanel value={value} index={0}>
        <ExcelFile data={state} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <DndProvider backend={HTML5Backend}>
          <HtmlForm data={state} />
        </DndProvider>
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <Preview data={state} />
      </CustomTabPanel>

      {showExportDialog && (
        <ExportTemplateDialog
          show={showExportDialog}
          close={() => setShowExportDialog(false)}
          onExport={exportFormDownload}
        />
      )}
      {showImportDialog && (
        <ImportTemplateDialog
          show={showImportDialog}
          close={() => setShowImportDialog(false)}
          templateId={state?.id}
          isClientForm={state?.clientId ? true : false}
        />
      )}

      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
      {(loading || showLoading) && <Loading />}
    </>
  );
}
